import React, { useState } from "react";
import { Button, Dropdown, Modal } from "react-bootstrap";
import { LuDownload } from "react-icons/lu";
import IconExcel from "../../../images/dashboard/icon-excel.png";
import IconPdf from "../../../images/dashboard/icon-pdf.png";
import axios from "axios";
import apiConfig from "../../credentials/apiConfig";
import { toast } from "react-toastify";
import PDFViewer from "./PdfViewer";


const DocumentExportTools = ({
  engagementId,
  documentType,
  documentData,
  showViewOptions = true,
  showDownloadOptions = true,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfTitle, setPdfTitle] = useState(`${documentType.toUpperCase()} Report`);
  const [viewPdfLoading, setViewPdfLoading] = useState(false);
  
  // Modal state for showing PDF ready
  const [showPdfReadyModal, setShowPdfReadyModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);


  // Handle View PDF
  const handleViewPDFFromAPI = async () => {
    setViewPdfLoading(true);
    setShowPdfReadyModal(false);
    
    try {
      const authToken = localStorage.getItem("authToken");
      
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/exportPARFAR/${engagementId}`,
        { type: documentType.toLowerCase(), format: 'pdf' },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          timeout: 180000
        }
      );
      
      if (response.data && response.data.downloadUrl) {
        // ✅ Always show modal - don't auto-open
        setPdfUrl(response.data.downloadUrl);
        setShowPdfReadyModal(true);
        
        if (onSuccess) onSuccess('pdf-view');
      } else {
        toast.error("No PDF URL returned from server");
      }
    } catch (err) {
      console.error("View PDF error:", err);
      
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        toast.error("Request timed out. Please try again.", { autoClose: 6000 });
      } else if (err.response) {
        toast.error(err.response.data?.message || "Failed to generate PDF", { autoClose: 5000 });
      } else if (err.request) {
        toast.error("No response from server. Please check your connection.", { autoClose: 5000 });
      } else {
        toast.error("Failed to generate PDF. Please try again.");
      }
    } finally {
      setViewPdfLoading(false);
    }
  };

  // Handle Download PDF
  const handleDownloadPDF = async () => {
    const authToken = localStorage.getItem("authToken");
    setShowPdfReadyModal(false);
    
    try {
      setIsLoading(true);
  
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/exportPARFAR/${engagementId}`,
        { type: documentType.toLowerCase(), format: 'pdf' },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          timeout: 180000
        }
      );
      
      if (response.data && response.data.downloadUrl) {
        // ✅ Always show modal - don't auto-open
        setPdfUrl(response.data.downloadUrl);
        setShowPdfReadyModal(true);
        
        if (onSuccess) onSuccess('pdf-download');
      } else {
        toast.error("No PDF URL returned from server");
      }
    } catch (error) {
      console.error("Download PDF error:", error);
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error("Request timed out. Please try again.", { autoClose: 6000 });
      } else if (error.response) {
        toast.error(error.response.data?.message || "Failed to download PDF", { autoClose: 5000 });
      } else if (error.request) {
        toast.error("No response from server. Please check your connection.", { autoClose: 5000 });
      } else {
        toast.error("Failed to download PDF. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };
  


  // Handle Excel download
  const handleDownloadExcel = async () => {
    const authToken = localStorage.getItem("authToken");

    try {
      setIsLoading(true);

      const response = await fetch(`${apiConfig.apiBaseURL}/exportPARFAR/${engagementId}`, {
        method: "POST",
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          "Authorization": `Bearer ${authToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ type: documentType.toLowerCase(), format: 'excel' }),
        signal: AbortSignal.timeout(120000)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const base64 = await response.text();

      if (!base64 || base64.trim() === '') {
        throw new Error("Received empty response from server");
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length).fill().map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", `${documentType.toUpperCase()}_Report.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Excel downloaded successfully");

      if (onSuccess) onSuccess('excel-download');
    } catch (error) {
      console.error("Download Excel error:", error);
      
      if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
        toast.error("Request timed out. Please try again.", { autoClose: 5000 });
      } else if (error.message.includes('HTTP error')) {
        toast.error(`Server error: ${error.message}`, { autoClose: 5000 });
      } else {
        toast.error("Failed to download Excel. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      {showViewOptions && (
        <div className="my-auto docs">
          <span className="me-2 fw-bold" style={{ fontSize: '14px' }}>View</span>

          <Button
            onClick={handleViewPDFFromAPI}
            className="me-2 border rounded px-2"
            style={{ background: 'none', border: 'none' }}
            disabled={viewPdfLoading}
          >
            {viewPdfLoading ? (
              <div className="spinner-border text-primary" role="status" style={{ width: '16px', height: '16px' }} />
            ) : (
              <img src={IconPdf} alt="PDF" width="30" height="30" />
            )}
          </Button>
        </div>
      )}

      {showDownloadOptions && (
        <Dropdown className="ms-2 me-2">
          <Dropdown.Toggle
            variant="primary"
            className="d-flex align-items-center"
            style={{ padding: '10px 30px' }}
            disabled={isLoading}
          >
            <LuDownload size={20} className="me-2" />
            {isLoading ? "Processing..." : "Download"}
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow border-0">
            <Dropdown.Item onClick={handleDownloadPDF} className="py-2" disabled={isLoading}>
              <img src={IconPdf} alt="PDF" width="16" height="16" className="me-2" />
              Download PDF
            </Dropdown.Item>
            <Dropdown.Item onClick={handleDownloadExcel} className="py-2" disabled={isLoading}>
              <img src={IconExcel} alt="Excel" width="16" height="16" className="me-2" />
              Download Excel
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      )}

      {/* PDF Ready Modal */}
      <Modal show={showPdfReadyModal} onHide={() => setShowPdfReadyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>PDF Ready!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            Your {documentType.toUpperCase()} report has been generated successfully.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              window.open(pdfUrl, '_blank', 'noopener,noreferrer');
              setShowPdfReadyModal(false);
            }}
            style={{ padding: '12px 40px', fontSize: '16px' }}
          >
            Open PDF in New Tab
          </Button>
        </Modal.Body>
      </Modal>

      <PDFViewer
        show={showPdfViewer}
        onHide={() => setShowPdfViewer(false)}
        pdfBlob={pdfBlob}
        title={pdfTitle}
      />
    </>
  );
};

export default DocumentExportTools;